// Content of the /discover landing, taken 1:1 from the designer's Figma frame
// "Advertising_web_last" (3DWay file, node 28331:31198, 2026-09-17/18). The bundled collections
// are what the chips show; a typed search goes to the backend (discover-featured-block.ts).
// Every image under src/assets/images/discover/ is
// one of our own dev renders (portraits, 480 px, webp q72) — the ids below are what
// "Explore …" will send once wired. The designer's Figma exports were matched back to the
// catalogue by fingerprint (~/Projects/landing-probes/figma/shipped-assets.json); the rest were
// picked from the dev search results by hand (~/Projects/landing-probes/figma/cards-manifest.json).
// Two pictures the exact-match search cannot reach (the mask rack, the Egyptian statues) ship as
// the Figma export of the same render.
//
// The designer's texts are kept verbatim, including "Veniece": that typo is the real query
// title on dev (58 assets), so the search only finds it spelled that way.

export interface DiscoverImage {
  // File name under src/assets/images/discover/<file>.webp.
  file: string;
  alt: string;
  // Spanner asset id on dev; null where the render could not be matched.
  assetId: string | null;
}

export function discoverImageUrl(image: DiscoverImage): string {
  return `assets/images/discover/${image.file}.webp`;
}

// Collections the featured block ("A … collection. A hundred possibilities.") can show. The
// "Try …" chips, the hero link and the covers switch it; the page starts on Veniece.
export type DiscoverFeaturedId = 'veniece' | 'florence' | 'ancient-egypt' | 'viking';

export interface DiscoverFeaturedCollection {
  id: DiscoverFeaturedId;
  name: string;
  // Exact query title for {webBackend}/ai-search/search.
  query: string;
  // First line of the featured title (the article changes: "A Viking…", "An Ancient Egypt…").
  headline: string;
  aside: string;
  // Assets the search returns for the query — every group summed (Ancient Egypt and Viking
  // span several query groups on dev); dev counts of 2026-09-17.
  count: number;
  // Seven portrait cards, left to right.
  cards: DiscoverImage[];
}

export interface DiscoverHero {
  title: string;
  caption: string;
  // Phones show the caption and the link on one line; this is what fits next to the link.
  captionShort: string;
  exploreLabel: string;
  // Which featured collection the caption link shows.
  featured: DiscoverFeaturedId;
  landscape: DiscoverImage;
  portrait: DiscoverImage;
}

// One cover in "Keep the ideas that belong together." — opens the collection in the library.
export interface DiscoverCollectionCover {
  name: string;
  query: string;
  count: number;
  // Cover collage: [large left tile, top-right tile, bottom-right tile].
  cover: [DiscoverImage, DiscoverImage, DiscoverImage];
}

export const DISCOVER_HERO: DiscoverHero = {
  title: 'Basilica of San Lorenzo, Florence',
  caption: 'Basilica of San Lorenzo, Florence',
  captionShort: 'San Lorenzo, Florence',
  exploreLabel: 'Explore the ‘Florence’ collection',
  featured: 'florence',
  landscape: { file: 'hero-landscape', alt: 'Basilica of San Lorenzo, Florence — concept render', assetId: 'FwLv2kNStKrmxjNbZiIwsI__gEJrGpBzniQROMGIGmNk__4' },
  portrait: { file: 'hero-portrait', alt: 'Basilica of San Lorenzo, Florence — portrait render', assetId: 'FwLv2kNStKrmxjNbZiIwsI__gEJrGpBzniQROMGIGmNk__4' },
};

const VENIECE: DiscoverFeaturedCollection = {
  id: 'veniece',
  name: 'Veniece',
  query: 'Veniece',
  headline: 'A Veniece collection.',
  aside: 'Explore the architecture, boats and working details that could shape your own scene.',
  count: 58,
  // Design order (Figma "Frame 5").
  cards: [
    { file: 'card-veniece-1', alt: 'Palazzo, Venice', assetId: 'vY6BqSX6PETZhKS7BFxk__q__1779382217868__3yajqf__3pqGM3ztKmlmU18DCCCU__1' },
    { file: 'card-veniece-2', alt: 'Basilica di San Marco', assetId: 'vY6BqSX6PETZhKS7BFxk__q__1779382217868__3yajqf__WKex2OzaGfO8d3hhIxzC__4' },
    { file: 'card-veniece-3', alt: 'Ca’ d’Oro Palace, Venice', assetId: 'AINxfyh76xEIhQPa3G3r1I__whziBsOuSl8yTfPUNphm__7' },
    { file: 'card-veniece-4', alt: 'House on Canal Grande', assetId: 'vY6BqSX6PETZhKS7BFxk__q__1779382217868__3yajqf__3pqGM3ztKmlmU18DCCCU__2' },
    { file: 'card-veniece-5', alt: 'Traditional gondola', assetId: 'vY6BqSX6PETZhKS7BFxk__q__1779382217868__3yajqf__jPsJeweDtrEbydNo1NN7__1' },
    { file: 'card-veniece-6', alt: 'Venetian carnival masks on a wooden rack', assetId: null },
    { file: 'card-veniece-7', alt: 'Palazzo Tiepolo', assetId: 'vY6BqSX6PETZhKS7BFxk__q__1779382217868__3yajqf__3pqGM3ztKmlmU18DCCCU__3' },
  ],
};

const FLORENCE: DiscoverFeaturedCollection = {
  id: 'florence',
  name: 'Florence',
  query: 'Florence',
  headline: 'A Florence collection.',
  aside: 'Explore the palazzi, bridges and street details that could shape your own scene.',
  count: 30,
  cards: [
    { file: 'card-florence-1', alt: 'Cathedral of Santa Maria del Fiore', assetId: 'AINxfyh76xEIhQPa3G3r1I__whziBsOuSl8yTfPUNphm__4' },
    { file: 'card-florence-2', alt: 'Ponte Vecchio', assetId: 'FwLv2kNStKrmxjNbZiIwsI__gEJrGpBzniQROMGIGmNk__1' },
    { file: 'card-florence-3', alt: 'Vintage Florentine bicycle', assetId: 'FwLv2kNStKrmxjNbZiIwsI__LsIp7vDUvSkewjKDzzcs__1' },
    { file: 'card-florence-4', alt: 'Palazzo Vecchio', assetId: 'FwLv2kNStKrmxjNbZiIwsI__gEJrGpBzniQROMGIGmNk__2' },
    { file: 'card-florence-5', alt: 'Neptune Fountain', assetId: 'FwLv2kNStKrmxjNbZiIwsI__gEJrGpBzniQROMGIGmNk__7' },
    { file: 'card-florence-6', alt: 'Ornate Florentine street lamp', assetId: 'FwLv2kNStKrmxjNbZiIwsI__LsIp7vDUvSkewjKDzzcs__2' },
    { file: 'card-florence-7', alt: 'Santa Maria Novella', assetId: 'FwLv2kNStKrmxjNbZiIwsI__gEJrGpBzniQROMGIGmNk__8' },
  ],
};

const ANCIENT_EGYPT: DiscoverFeaturedCollection = {
  id: 'ancient-egypt',
  name: 'Ancient Egypt',
  query: 'Ancient Egypt',
  headline: 'An Ancient Egypt collection.',
  aside: 'Explore the temples, statues and everyday objects that could shape your own scene.',
  count: 146,
  cards: [
    { file: 'card-egypt-1', alt: 'Egyptian temple facade', assetId: 'GqE0Rt62tuTjWgjxz8Cbcm__run_af4814fe-0540-4030-a6eb-3941d77868a8__3' },
    { file: 'card-egypt-2', alt: 'Nefertiti bust', assetId: 'vY6BqSX6PETZhKS7BFxk__4__run_63a02390-29f4-4d79-8684-4b58965e1a6f__5' },
    { file: 'card-egypt-3', alt: 'Egyptian war chariot', assetId: 'vY6BqSX6PETZhKS7BFxk__29__run_8471f1df-e7ca-4b58-92c0-0fd72afb4613__29' },
    { file: 'card-egypt-4', alt: 'Guardian sphinx statue', assetId: 'vY6BqSX6PETZhKS7BFxk__q__1779863025564__hef990__run_0ab7731a-2f5f-43f8-ac01-1fa725f5f7b9__7' },
    { file: 'card-egypt-5', alt: 'Abu Simbel temple facade', assetId: 'vY6BqSX6PETZhKS7BFxk__q__1779426410638__k4kio3__run_5e91ae8f-732c-498d-b74c-4c678eb2441c__29' },
    { file: 'card-egypt-6', alt: 'Blue-painted ibex amphora', assetId: 'vY6BqSX6PETZhKS7BFxk__4__run_0b399509-cfff-4d23-8d0a-2e9e5a5ca9e8__3' },
    { file: 'card-egypt-7', alt: 'Ceremonial boat', assetId: 'vY6BqSX6PETZhKS7BFxk__4__run_bccb5b9e-608b-4903-b661-02bd95ed11e4__1' },
  ],
};

const VIKING: DiscoverFeaturedCollection = {
  id: 'viking',
  name: 'Viking',
  query: 'Viking',
  headline: 'A Viking collection.',
  aside: 'Explore the longships, longhouses and gear that could shape your own scene.',
  count: 107,
  cards: [
    { file: 'card-viking-1', alt: 'Drakkar longship', assetId: 'M7FkGOQUpTYKX8b4xYCkUZ__run_9e87e9b7-553a-4288-87d5-3037e9ba905c__2' },
    { file: 'card-viking-2', alt: 'Traditional Viking house', assetId: 'vY6BqSX6PETZhKS7BFxk__q__1779382013502__9e53s8__run_5ba82830-9d21-46f0-b117-6dce99b52e23__1' },
    { file: 'card-viking-3', alt: 'Viking jarl chieftain', assetId: 'KTy3Igo6zg6A4dH0aw5CtG__run_6ed4e11f-f76b-45e9-9fcf-05757a3d43b4__1' },
    { file: 'card-viking-4', alt: 'Decorated Valsgärde helmet', assetId: 'M7FkGOQUpTYKX8b4xYCkUZ__run_758e63db-d9ff-482c-9f2b-71b2cf131155__1' },
    { file: 'card-viking-5', alt: 'Timber watchtower', assetId: 'vY6BqSX6PETZhKS7BFxk__q__1779794970931__9qeoxn__cTl42neTjUDtzQjK8lQl__4' },
    { file: 'card-viking-6', alt: 'The Rök runestone', assetId: 'e8hNFI7ULxuId4gc6fYmR2__run_8db9c956-ae8c-46af-87c7-9fbcb105979d__1' },
    { file: 'card-viking-7', alt: 'Norse ox cart', assetId: 'vY6BqSX6PETZhKS7BFxk__q__1779794970931__9qeoxn__cTl42neTjUDtzQjK8lQl__11' },
  ],
};

export const DISCOVER_FEATURED: ReadonlyArray<DiscoverFeaturedCollection> = [VENIECE, FLORENCE, ANCIENT_EGYPT, VIKING];

// "A Veniece collection. A hundred possibilities." is what the page opens on.
export const DISCOVER_DEFAULT_FEATURED: DiscoverFeaturedId = 'veniece';

// "Keep the ideas that belong together." — three collection covers.
export const DISCOVER_COLLECTION_COVERS: ReadonlyArray<DiscoverCollectionCover> = [
  {
    name: VENIECE.name,
    query: VENIECE.query,
    count: VENIECE.count,
    cover: [
      { file: 'cover-veniece-a', alt: 'Basilica di San Marco', assetId: 'vY6BqSX6PETZhKS7BFxk__q__1779382217868__3yajqf__WKex2OzaGfO8d3hhIxzC__4' },
      { file: 'cover-veniece-b', alt: 'Traditional gondola', assetId: 'vY6BqSX6PETZhKS7BFxk__q__1779382217868__3yajqf__jPsJeweDtrEbydNo1NN7__1' },
      { file: 'cover-veniece-c', alt: 'Palazzo, Venice', assetId: 'vY6BqSX6PETZhKS7BFxk__q__1779382217868__3yajqf__3pqGM3ztKmlmU18DCCCU__1' },
    ],
  },
  {
    name: ANCIENT_EGYPT.name,
    query: ANCIENT_EGYPT.query,
    count: ANCIENT_EGYPT.count,
    cover: [
      { file: 'cover-egypt-a', alt: 'Ceremonial boat with a mummy', assetId: 'vY6BqSX6PETZhKS7BFxk__4__run_7cdb2cbd-6729-40d6-bbaa-6b19fb705a10__1' },
      { file: 'cover-egypt-b', alt: 'Egyptian nobles', assetId: 'vY6BqSX6PETZhKS7BFxk__29__run_ff23a5b7-ffb5-4756-9a49-1a28413e3f75__17' },
      { file: 'cover-egypt-c', alt: 'Egyptian painted statues', assetId: null },
    ],
  },
  {
    name: VIKING.name,
    query: VIKING.query,
    count: VIKING.count,
    cover: [
      { file: 'cover-viking-a', alt: 'Group of Vikings in attack', assetId: 'KTy3Igo6zg6A4dH0aw5CtG__run_38a31a49-0ec8-4b3c-8522-0cb8bd97ca8b__1' },
      { file: 'cover-viking-b', alt: 'Longship in for repairs', assetId: 'vY6BqSX6PETZhKS7BFxk__q__1779382013502__9e53s8__run_fdea081a-c7c7-44eb-98e2-55824bd0df4c__1' },
      { file: 'cover-viking-c', alt: 'Viking cart', assetId: 'vY6BqSX6PETZhKS7BFxk__q__1779794970931__9qeoxn__run_17d95a95-d70c-4dec-8676-620b5be5028a__3' },
    ],
  },
];

// "Try …" chips above the hero search (Figma "Search", 2026-09-18): each one shows its
// collection in the featured block. All three are real query titles on dev.
export const DISCOVER_TRY_QUERIES: ReadonlyArray<{ label: string; featured: DiscoverFeaturedId }> = [
  { label: 'Florence', featured: 'florence' },
  { label: 'Ancient Egypt', featured: 'ancient-egypt' },
  { label: 'Viking', featured: 'viking' },
];

export const DISCOVER_FAQ: ReadonlyArray<{ question: string; answer: string }> = [
  {
    question: 'What is free?',
    answer: 'The proposed beta offers free discovery, saving and sharing. Requests for new references depend on review capacity. Scoped paid requests can be introduced once costs and demand are understood.',
  },
  {
    question: 'Are these images AI-generated?',
    answer: 'Yes. The six concept images shown here were AI-generated and approved by a 3DWay artist. Rendered-model videos are a different content type in the same Library.',
  },
  {
    question: 'Does a reference include a 3D model?',
    answer: 'A concept image is visual inspiration. A rendered-model video shows 3D work. Neither by itself promises a downloadable model or verified construction detail.',
  },
  {
    question: 'When will a requested collection be ready?',
    answer: 'The proposed flow confirms timing when a request is accepted. Artist review comes before delivery. This preview demonstrates the steps; it does not generate references or send email.',
  },
];
